'use client';

import { usePathname } from 'next/navigation';
import { useTranslation } from 'react-i18next';

import { Breadcrumb } from '@sola/ark-skippy/molecules/breadcrumb/breadcrumb.js';
import { keys } from '@sola/contentful-model/keys.js';
import { getArticlePath } from '@sola/contentful-model/lib/article.js';
import { articlesUrlBase } from '@sola/contentful-model/lib/routes.js';
import { LOCALES_CODES } from '@sola/contentful-model/lib/types.js';
import { ArticleResponseDto } from '@sola/schema/lib/contentful.js';
import { trailingSlash } from '@sola/utils/string.js';

import { RenderArticle } from './RenderArticle';

const ArticleInner = ({
  article,
  locale,
  experiments,
}: {
  article: ArticleResponseDto;
  locale: LOCALES_CODES;
  experiments?: string[];
}) => {
  const data = ArticleResponseDto.parse(article);
  const { t } = useTranslation(['skippy']);

  const path = usePathname();

  const urlBase = data.taxonomyArticleType
    ? articlesUrlBase[data.taxonomyArticleType as keyof typeof articlesUrlBase]
    : '#article';

  // @TODO: Extend the getBreadcrumbFromPage function?
  const breadcrumbs = [
    {
      title: t(`skippy:home`, 'Home'),
      link: `/`,
    },
  ];

  if (data.taxonomyArticleType === keys.articleTypes.pressRelease) {
    breadcrumbs.push({
      title: t(`skippy:pressCenter`, 'Press Center'),
      link: urlBase.split('/').slice(0, -1).join('/'),
    });
  }

  breadcrumbs.push({
    title:
      t(
        `articles:taxonomy.articleTypes.${data.taxonomyArticleType}.pluralName`,
      ) || 'Article',

    link: urlBase,
  });

  breadcrumbs.push({
    title: article.pageTitle || '',
    link: trailingSlash(getArticlePath(article.slug, data.taxonomyArticleType)),
  });

  return (
    <>
      <Breadcrumb breadcrumbs={breadcrumbs} />
      <main id="main">
        <RenderArticle
          article={data}
          locale={locale}
          path={path}
          experiments={experiments}
        />
      </main>
    </>
  );
};

export const ArticlePage = ({
  article,
  locale,
  experiments,
}: {
  article: ArticleResponseDto;
  locale: LOCALES_CODES;
  experiments?: string[];
}) => {
  return (
    <ArticleInner article={article} locale={locale} experiments={experiments} />
  );
};
